import React, { useCallback, useEffect, useState } from 'react';
import { HelpCircle, Loader2, MessageCircleQuestion, ShieldCheck, ThumbsUp, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { questionsAPI, authAPI } from '../services/api';
import { useT, i18n } from '../services/i18n';

const fmt = (d) => new Date(d).toLocaleDateString(i18n.locale(), { day: 'numeric', month: 'long', year: 'numeric' });

const QuestionItem = ({ q, onVote }) => {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const vote = async () => {
    setBusy(true);
    try { onVote(q.id, await questionsAPI.toggleHelpful(q.id)); } catch (e) { toast.error(e.response?.data?.detail || t('questions.voteError')); } finally { setBusy(false); }
  };
  return (
    <li className="py-4" data-testid={`question-${q.id}`}>
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">Q</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900" data-testid={`question-text-${q.id}`}>{q.question}</p>
          <p className="text-xs text-gray-400 mt-0.5">{q.author_name} · {fmt(q.created_at)}{q.is_mine && <span className="ml-2 text-orange-600 font-medium">{t('questions.yours')}</span>}</p>
          {q.status === 'pending' ? (
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1" data-testid={`question-pending-${q.id}`}><Clock size={12} /> {t('questions.pending')}</p>
          ) : (
            <div className="mt-3 flex gap-3" data-testid={`question-answer-${q.id}`}>
              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">R</div>
              <div className="flex-1 min-w-0 bg-orange-50/70 border-l-4 border-orange-400 rounded-r-lg p-3">
                <p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5"><ShieldCheck size={13} className="text-orange-500" /> {t('faq.answerFrom', { author: q.answer.author })} <span className="font-normal text-gray-500">· {fmt(q.answer.updated_at || q.answer.created_at)}</span></p>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{q.answer.message}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                  <span>{t('questions.helped')}</span>
                  <button type="button" onClick={vote} disabled={busy} data-testid={`question-helpful-${q.id}`} aria-pressed={q.voted}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium transition-colors ${q.voted ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400 hover:text-orange-600'}`}>
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <ThumbsUp size={12} className={q.voted ? 'fill-white' : ''} />} Utile{q.helpful_count > 0 && <span data-testid={`question-helpful-count-${q.id}`}>({q.helpful_count})</span>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

const ProductQuestions = ({ productId, productName }) => {
  const { t } = useT();
  const [data, setData] = useState({ questions: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ question: '', name: '', email: '' });
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const isAuth = authAPI.isAuthenticated();

  const load = useCallback(() => questionsAPI.forProduct(productId).then(setData).catch(() => {}).finally(() => setLoading(false)), [productId]);
  useEffect(() => { setLoading(true); setSuccess(''); load(); }, [load]);
  useEffect(() => {
    const onOpen = () => { setOpen(true); setSuccess(''); setTimeout(() => document.querySelector('[data-testid="question-input"]')?.focus(), 400); };
    window.addEventListener('open-question-form', onOpen);
    return () => window.removeEventListener('open-question-form', onOpen);
  }, []);

  const submit = async () => {
    setError('');
    if (form.question.trim().length < 10) { setError(t('questions.errShort')); return; }
    if (!isAuth && form.name.trim().length < 2) { setError(t('questions.errName')); return; }
    if (!isAuth && !/\S+@\S+\.\S+/.test(form.email)) { setError(t('questions.errEmail')); return; }
    setSending(true);
    try {
      const res = await questionsAPI.ask(productId, isAuth ? { question: form.question } : form);
      setSuccess(res.message);
      setForm({ question: '', name: '', email: '' });
      setOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Envoi impossible pour le moment");
    } finally {
      setSending(false);
    }
  };

  const applyVote = (id, res) => setData(d => ({ ...d, questions: d.questions.map(q => (q.id === id ? { ...q, ...res } : q)) }));

  return (
    <section id="questions" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-24" data-testid="product-questions">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <MessageCircleQuestion className="text-orange-500" size={22} /> {t('questions.title')}
          <span className="text-sm font-normal text-gray-500" data-testid="questions-count">{loading ? '' : `(${data.count})`}</span>
        </h2>
        {!open && <Button size="sm" onClick={() => { setOpen(true); setSuccess(''); }} data-testid="ask-question-button" className="bg-orange-500 hover:bg-orange-600"><HelpCircle size={15} className="mr-1.5" /> {t('questions.ask')}</Button>}
      </div>

      {open && (
        <div className="border border-orange-200 bg-orange-50/40 rounded-lg p-4 mb-5" data-testid="question-form">
          <p className="text-sm font-semibold text-gray-900 mb-2">{t('questions.yourQuestionOn', { name: productName })}</p>
          <textarea data-testid="question-input" value={form.question} onChange={(e) => setForm(f => ({ ...f, question: e.target.value }))} rows={3} maxLength={300}
            placeholder={t('questions.placeholder')}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          <p className="text-[11px] text-gray-400 text-right">{form.question.length}/300</p>
          {!isAuth && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <Input data-testid="question-name-input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('questions.name')} maxLength={80} />
              <Input data-testid="question-email-input" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder={t('questions.email')} />
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">{t('questions.notice')}{isAuth ? t('questions.notifyEmail') : t('questions.emailPrivate')}</p>
          {error && <p className="text-sm text-red-600 mt-2" data-testid="question-error">{error}</p>}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="question-cancel">{t('common.cancel')}</Button>
            <Button size="sm" onClick={submit} disabled={sending} data-testid="question-submit" className="bg-orange-500 hover:bg-orange-600">{sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} {t('questions.send')}</Button>
          </div>
        </div>
      )}
      {success && <p className="text-sm text-green-700 flex items-center gap-2 mb-4" data-testid="question-success"><ShieldCheck size={16} /> {success}</p>}

      {loading ? <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div>
        : data.questions.length === 0 ? <p className="text-sm text-gray-500" data-testid="questions-empty">{t('questions.empty')}</p>
        : <ul className="divide-y" data-testid="questions-list">{data.questions.map(q => <QuestionItem key={q.id} q={q} onVote={applyVote} />)}</ul>}
    </section>
  );
};

export default ProductQuestions;
